import { Component, EventEmitter, Input, Output } from '@angular/core';
import { EmailMetrics, EmailSequence, LinkedInMetrics } from 'src/app/models/email-sequence.model';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { CardModule } from 'primeng/card';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';


@Component({
  selector: 'app-sequence-metrics-details-modal',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    CardModule,
    BadgeModule,
    ButtonModule,
    ProgressBarModule
  ],
  templateUrl: './sequence-metrics-details-modal.component.html',
  styleUrl: './sequence-metrics-details-modal.component.scss'
})
export class SequenceMetricsDetailsModalComponent {

  @Input() visible: boolean = false;
  @Input() sequence: EmailSequence | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();

  onClose() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  getStatusSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warn';
      case 'completed': return 'info';
      case 'draft': return 'danger';
      default: return 'info';
    }
  }
  getStatusIcon(status: string): string {
    switch (status) {
      case 'active': return 'text-green-500';
      case 'paused': return 'text-yellow-500';
      case 'completed': return 'text-blue-500';
      case 'draft': return 'text-red-500';
      default: return 'text-gray-500';
    }
  }

  getLinkedInMetrics() {
    if (!this.sequence?.metrics) return [];

    const metrics = this.sequence.metrics as LinkedInMetrics;
    return [
      { label: 'Profile Views', value: metrics.profileViews, icon: 'pi pi-eye', isPercentage: false },
      { label: 'Post Likes', value: metrics.postLikes, icon: 'pi pi-heart', isPercentage: false },
      { label: 'Follows', value: metrics.follows, icon: 'pi pi-user-plus', isPercentage: false },
      { label: 'Messages Sent', value: metrics.messagesSent, icon: 'pi pi-send', isPercentage: false },
      { label: 'Message Started', value: metrics.totalMessageStarted, icon: 'pi pi-comment', isPercentage: false },
      { label: 'Message Replies', value: metrics.totalMessageReplies, icon: 'pi pi-reply', isPercentage: false },
      { label: 'InMail Sent', value: metrics.inmailMessagesSent, icon: 'pi pi-envelope', isPercentage: false },
      { label: 'InMail Started', value: metrics.totalInmailStarted, icon: 'pi pi-inbox', isPercentage: false },
      { label: 'InMail Replies', value: metrics.totalInmailReplies, icon: 'pi pi-reply', isPercentage: false },
      { label: 'Connections Sent', value: metrics.connectionsSent, icon: 'pi pi-users', isPercentage: false },
      { label: 'Connections Accepted', value: metrics.connectionsAccepted, icon: 'pi pi-check-circle', isPercentage: false },
      { label: 'Message Reply Rate', value: metrics.messageReplyRate * 100, icon: 'pi pi-percentage', isPercentage: true },
      { label: 'InMail Reply Rate', value: metrics.inMailReplyRate * 100, icon: 'pi pi-percentage', isPercentage: true },
      { label: 'Connection Acceptance Rate', value: metrics.connectionAcceptanceRate * 100, icon: 'pi pi-percentage', isPercentage: true }
    ];
  }

  getEmailMetrics() {
    if (!this.sequence?.metrics) return [];

    const metrics = this.sequence.metrics as EmailMetrics;
    console.log('METRICS TO REDO ',metrics);
    
    return [
      { label: 'Opens', value: metrics.openCount, icon: 'pi pi-eye', isPercentage: false },
      { label: 'Unique Opens', value: metrics.openCountUnique, icon: 'pi pi-eye', isPercentage: false },
      { label: 'Opens by Step', value: metrics.openCountUniqueByStep, icon: 'pi pi-list', isPercentage: false },
      { label: 'Link Clicks', value: metrics.linkClickCount, icon: 'pi pi-external-link', isPercentage: false },
      { label: 'Unique Clicks', value: metrics.linkClickCountUnique, icon: 'pi pi-external-link', isPercentage: false },
      { label: 'Clicks by Step', value: metrics.linkClickCountUniqueByStep, icon: 'pi pi-list', isPercentage: false },
      { label: 'Replies', value: metrics.replyCount, icon: 'pi pi-reply', isPercentage: false },
      { label: 'Unique Replies', value: metrics.replyCountUnique, icon: 'pi pi-reply', isPercentage: false },
      { label: 'Replies by Step', value: metrics.replyCountUniqueByStep, icon: 'pi pi-list', isPercentage: false },
      { label: 'Bounced', value: metrics.bouncedCount, icon: 'pi pi-exclamation-triangle', isPercentage: false },
      { label: 'Unsubscribed', value: metrics.unsubscribedCount, icon: 'pi pi-times-circle', isPercentage: false },
      { label: 'Completed', value: metrics.completedCount, icon: 'pi pi-check-circle', isPercentage: false },
      { label: 'Emails Sent', value: metrics.emailsSentCount, icon: 'pi pi-send', isPercentage: false },
      { label: 'New Leads', value: metrics.newLeadsContactedCount, icon: 'pi pi-user-plus', isPercentage: false },
      { label: 'Opportunities', value: metrics.totalOpportunities, icon: 'pi pi-star', isPercentage: false },
      { label: 'Opportunity Value', value: metrics.totalOpportunityValue, icon: 'pi pi-dollar', isPercentage: false },
      { label: 'Interested', value: metrics.totalInterested, icon: 'pi pi-thumbs-up', isPercentage: false },
      { label: 'Meetings Booked', value: metrics.totalMeetingBooked, icon: 'pi pi-calendar-plus', isPercentage: false },
      { label: 'Meetings Completed', value: metrics.totalMeetingCompleted, icon: 'pi pi-calendar-times', isPercentage: false },
      { label: 'Closed', value: metrics.totalClosed, icon: 'pi pi-check', isPercentage: false }
    ];
  }

  getOpenRate(metrics:any) {
    if (metrics.openCount === 0 || metrics.emailsSentCount) return 0;
    return (metrics.openCount / metrics.emailsSentCount) * 100;
  }

  getReplyRate(metrics:any) {
    if (metrics.emailsSentCount) return 0;
    return (metrics.replyCount / metrics.emailsSentCount) * 100;
  }
}
